/**
 * Shared building blocks for the Phase 4 T06 kiosk builder pages.
 *
 * Hard rule (design doc §"T06 — admin-ui template builder"): geometry and ID
 * bounds are IMPORTED from `@cluster/kiosk-renderer` / `@cluster/types` —
 * never re-hardcoded here. Widget labels/min-sizes come from
 * `GET /v1/kiosks/widgets` at runtime; this module contains no catalog copy.
 * No money computation lives in the kiosk pages (money is opaque
 * minor-unit display strings end to end).
 */
import { Link } from 'react-router-dom';
import { FROZEN_RENDER_GEOMETRY as GEOMETRY } from '@cluster/kiosk-renderer';
import type {
  KioskLayoutWidget,
  KioskRenderDocument,
  KioskVisibilityRule,
  KioskWidgetDescriptor,
} from '@cluster/types';
import { Button } from '@/components/ui/button';
import { apiErrorMessage } from '@/lib/apiError';

export type KioskSection = 'templates' | 'devices';

const destinations: Array<{ id: KioskSection; label: string }> = [
  { id: 'templates', label: 'Kiosk templates' },
  { id: 'devices', label: 'Kiosk devices' },
];

export function KioskNavigation({ active }: { active: KioskSection }) {
  return (
    <nav aria-label="Kiosk management" className="flex flex-wrap gap-2 border-b pb-3">
      {destinations.map(({ id, label }) => (
        <Button key={id} asChild variant={active === id ? 'secondary' : 'ghost'}>
          <Link to={`/kiosks/${id}`} aria-current={active === id ? 'page' : undefined}>{label}</Link>
        </Button>
      ))}
    </nav>
  );
}

export function KioskLoadError({ label, retry }: { label: string; retry: () => void }) {
  return (
    <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
      Could not load {label}. <Button type="button" variant="outline" onClick={retry}>Try again</Button>
    </div>
  );
}

/**
 * Server-declared failure text without ever fabricating a cause. Delegates to
 * the shared extractor so the platform envelope `{ error: { message, details } }`
 * is read correctly — the kiosk service reports validation failures as
 * `"Request validation failed"` plus per-field `details`, and dropping those
 * left the operator with an opaque rejection.
 */
export function kioskApiErrorText(error: unknown): string {
  return apiErrorMessage(error, 'The kiosk service rejected this request.');
}

export function formatKioskDate(value?: string | null): string {
  if (!value) return 'Not provided';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Date unavailable'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/** The frozen canvas the builder authors against (imported, never re-declared). */
export const CANVAS = GEOMETRY.canvas;

export interface PlacementIssue {
  field: 'x' | 'y' | 'w' | 'h' | 'zIndex' | 'widgetId';
  message: string;
}

/**
 * Validates one candidate placement against the frozen geometry contract
 * (`services/kiosks/README.md` §Widget catalog + `FROZEN_RENDER_GEOMETRY`):
 * integer origin-top-left CSS pixels on the 1920×1080 canvas, containment,
 * per-widget catalog `minSize`, `zIndex` bounds, ID trimming/length, and
 * uniqueness. Returns every issue found; the caller must refuse the edit.
 */
export function validateWidgetPlacement(
  candidate: KioskLayoutWidget,
  minSize: { w: number; h: number },
  siblings: readonly KioskLayoutWidget[],
): PlacementIssue[] {
  const issues: PlacementIssue[] = [];
  const { x, y, w, h } = candidate.position;

  if (candidate.widgetId.trim().length === 0) {
    issues.push({ field: 'widgetId', message: `Widget ID must not be empty.` });
  } else if (candidate.widgetId.trim().length > GEOMETRY.maxIdLength) {
    issues.push({ field: 'widgetId', message: `Widget ID exceeds the ${GEOMETRY.maxIdLength}-character cap.` });
  } else if (siblings.some((other) => other !== candidate && other.widgetId.trim() === candidate.widgetId.trim())) {
    issues.push({ field: 'widgetId', message: `Widget ID "${candidate.widgetId.trim()}" is already used in this layout.` });
  }

  for (const [key, value] of [['x', x], ['y', y], ['w', w], ['h', h]] as const) {
    if (!Number.isInteger(value)) {
      issues.push({ field: key, message: `${key} must be an integer CSS pixel value.` });
    }
  }
  if (Number.isInteger(x) && Number.isInteger(w) && x >= 0 && w >= 1) {
    if (x + w > CANVAS.width) {
      issues.push({ field: 'x', message: `Widget extends beyond the ${CANVAS.width}px canvas width. Keep x + w ≤ ${CANVAS.width}.` });
    }
  } else if (Number.isInteger(x) && x < 0) {
    issues.push({ field: 'x', message: `x must be at least 0 (origin is top-left).` });
  } else if (Number.isInteger(w) && w < 1) {
    issues.push({ field: 'w', message: `w must be at least 1.` });
  }
  if (Number.isInteger(y) && Number.isInteger(h) && y >= 0 && h >= 1) {
    if (y + h > CANVAS.height) {
      issues.push({ field: 'y', message: `Widget extends beyond the ${CANVAS.height}px canvas height. Keep y + h ≤ ${CANVAS.height}.` });
    }
  } else if (Number.isInteger(y) && y < 0) {
    issues.push({ field: 'y', message: `y must be at least 0 (origin is top-left).` });
  } else if (Number.isInteger(h) && h < 1) {
    issues.push({ field: 'h', message: `h must be at least 1.` });
  }

  if (Number.isInteger(w) && w < minSize.w) {
    issues.push({ field: 'w', message: `Width below this widget's catalog minimum (${minSize.w}px).` });
  }
  if (Number.isInteger(h) && h < minSize.h) {
    issues.push({ field: 'h', message: `Height below this widget's catalog minimum (${minSize.h}px).` });
  }

  if (!Number.isInteger(candidate.zIndex) || candidate.zIndex < GEOMETRY.zIndex.min || candidate.zIndex > GEOMETRY.zIndex.max) {
    issues.push({ field: 'zIndex', message: `zIndex must be an integer between ${GEOMETRY.zIndex.min} and ${GEOMETRY.zIndex.max} inclusive.` });
  }

  return issues;
}

/** Whole-layout checks the frozen contract imposes on every save/publish. */
export function validateLayoutForSave(
  layout: readonly KioskLayoutWidget[],
  descriptors: ReadonlyMap<string, KioskWidgetDescriptor>,
): string[] {
  const issues: string[] = [];
  if (layout.length > GEOMETRY.maxWidgets) {
    issues.push(`A layout holds at most ${GEOMETRY.maxWidgets} widgets.`);
  }
  const seen = new Set<string>();
  for (const widget of layout) {
    if (seen.has(widget.widgetId)) issues.push(`Duplicate widget ID "${widget.widgetId}".`);
    seen.add(widget.widgetId);
    const descriptor = descriptors.get(widget.type);
    if (!descriptor) {
      issues.push(`Widget type "${widget.type}" is not in the current catalog; remove it before saving.`);
      continue;
    }
    for (const issue of validateWidgetPlacement(widget, descriptor.minSize, layout)) {
      issues.push(`${widget.widgetId}: ${issue.message}`);
    }
  }
  return issues;
}

/** Comma/newline-separated ID lists → trimmed ID arrays (contract shape). */
export function parseIdList(value: string): string[] {
  return value
    .split(/[,\n]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

export function validateVisibilityRule(rule: KioskVisibilityRule): string[] {
  const issues: string[] = [];
  for (const [key, list] of [['allowIds', rule.allowIds], ['denyIds', rule.denyIds]] as const) {
    if (list.length > GEOMETRY.maxVisibilityIdsPerRule) {
      issues.push(`${key} exceeds the ${GEOMETRY.maxVisibilityIdsPerRule}-entry cap.`);
    }
    if (new Set(list).size !== list.length) {
      issues.push(`${key} must not contain duplicate record IDs.`);
    }
    const tooLong = list.filter((id) => id.length > GEOMETRY.maxIdLength);
    if (tooLong.length > 0) {
      issues.push(`${key} contains IDs longer than ${GEOMETRY.maxIdLength} characters.`);
    }
  }
  return issues;
}

/**
 * Assembles the draft render document in the frozen `schemaVersion: 1`
 * envelope. This is data shaping only — interpretation happens exclusively
 * through the shared `@cluster/kiosk-renderer` pipeline (`prepareRenderPlan`),
 * so builder preview and device rendering can never fork.
 */
export function buildDraftRenderDocument(
  templateId: string,
  version: number,
  layout: readonly KioskLayoutWidget[],
  visibility: KioskRenderDocument['visibility'],
): KioskRenderDocument {
  return {
    schemaVersion: 1,
    templateId,
    version,
    layout: layout.map((widget) => ({ ...widget })),
    visibility,
  };
}

/** Stable, contract-shaped widget ID for a newly placed widget. */
export function nextWidgetId(layout: readonly KioskLayoutWidget[], type: string): string {
  let index = layout.length + 1;
  while (layout.some((widget) => widget.widgetId === `${type}-${index}`)) index += 1;
  return `${type}-${index}`;
}

/**
 * Labeled dev/test fixture strings for the discount preview. These are
 * opaque display strings only — the builder never computes them and never
 * claims they are live data (design doc: fixtures isolated from production).
 */
export const DISCOUNT_FIXTURE_FIELDS = Object.freeze({
  originalPrice: 'EUR 4.50',
  discountedPrice: 'EUR 4.05',
  percentOff: '10% off',
  amountSaved: 'EUR 0.45',
  expiry: 'Ends Oct 10, 6:00 PM',
  promoLabel: 'Morning combo',
  conditions: 'Valid on Tuesdays before noon',
});
